// Where APD Copilot lives on screen. One component, three presentations, one
// set of business logic (CopilotConversation):
//
//   desktop (>= lg, 1024px)  docked right column beside the page; the page
//                            keeps working (split pane). Collapses to a
//                            launcher button.
//   tablet  (md..lg)         right-hand sheet over the page with a backdrop.
//   phone   (< md, 768px)    full screen, safe-area aware; its height follows
//                            the visual viewport so the on-screen keyboard
//                            never covers the composer.

import { useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useCopilot } from '@/lib/copilot-context';
import { CopilotConversation } from './copilot-conversation';

/** Docked on desktop, overlay below. Exported for tests. */
export const PANEL_CLASSES = {
  docked: 'hidden lg:flex flex-col w-[380px] xl:w-[420px] shrink-0 border-l border-border bg-surface min-h-0 min-w-0',
  overlay: 'lg:hidden fixed z-[45] flex flex-col bg-surface shadow-lg min-h-0 overflow-hidden '
    + 'inset-x-0 top-0 h-[var(--copilot-vvh,100dvh)] pt-[env(safe-area-inset-top)] '
    + 'md:inset-x-auto md:right-0 md:w-[min(440px,100vw)] md:border-l md:border-border',
  backdrop: 'lg:hidden hidden md:block fixed inset-0 z-[44] bg-black/40',
  launcher: 'fixed z-[40] right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-lg px-4 py-2.5 text-sm font-semibold hover:opacity-95',
} as const;

function useIsOverlay() {
  const [overlay, setOverlay] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023.98px)');
    const on = () => setOverlay(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return overlay;
}

/** Track the visual viewport (what is above the keyboard) as a CSS variable. */
function useVisualViewportHeight(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const vv = window.visualViewport;
    const root = document.documentElement;
    const set = () => root.style.setProperty('--copilot-vvh', `${Math.round(vv ? vv.height : window.innerHeight)}px`);
    set();
    vv?.addEventListener('resize', set);
    vv?.addEventListener('scroll', set);
    window.addEventListener('resize', set);
    return () => {
      vv?.removeEventListener('resize', set);
      vv?.removeEventListener('scroll', set);
      window.removeEventListener('resize', set);
      root.style.removeProperty('--copilot-vvh');
    };
  }, [active]);
}

/** Keep Tab inside the overlay; Escape closes. */
function useFocusTrap(ref: React.RefObject<HTMLElement>, active: boolean, onEscape: () => void) {
  useEffect(() => {
    if (!active) return;
    const prev = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onEscape(); return; }
      if (e.key !== 'Tab' || !ref.current) return;
      const f = [...ref.current.querySelectorAll<HTMLElement>('a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0]!; const last = f[f.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = overflow; prev?.focus?.(); };
  }, [active, onEscape, ref]);
}

/** The docked column. Rendered inside the app shell's main row. */
export function CopilotDock() {
  const { capability, open, setOpen } = useCopilot();
  const overlay = useIsOverlay();
  const ref = useRef<HTMLDivElement>(null);
  useVisualViewportHeight(open && overlay);
  useFocusTrap(ref, open && overlay, () => setOpen(false));
  useEffect(() => {
    if (!open || overlay) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && ref.current?.contains(document.activeElement)) setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, overlay, setOpen]);

  if (!capability.canUse) return null;
  if (!open) {
    return (
      <button className={PANEL_CLASSES.launcher} onClick={() => setOpen(true)} aria-label="Open APD Copilot">
        <Sparkles className="w-4 h-4" /><span>Copilot</span>
      </button>
    );
  }
  if (overlay) {
    return (
      <>
        <div className={PANEL_CLASSES.backdrop} onClick={() => setOpen(false)} aria-hidden="true" />
        <div ref={ref} role="dialog" aria-modal="true" aria-label="APD Copilot" className={PANEL_CLASSES.overlay}>
          <CopilotConversation onClose={() => setOpen(false)} />
        </div>
      </>
    );
  }
  return (
    <aside ref={ref} aria-label="APD Copilot" className={PANEL_CLASSES.docked}>
      <CopilotConversation onClose={() => setOpen(false)} />
    </aside>
  );
}

/** Header button for pages that have a header. */
export function CopilotHeaderButton() {
  const { capability, toggle, open } = useCopilot();
  if (!capability.canUse) return null;
  return (
    <button onClick={toggle} aria-pressed={open} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md border border-border hover:bg-surface-muted">
      <Sparkles className="w-3.5 h-3.5 text-primary" />Copilot
    </button>
  );
}
